import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ZendeskArticle, ZendeskCategory } from 'interfaces/ZendeskArticle';
import { Divider, ImageList, ImageListItem, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { DescriptionOutlined, FolderOpenOutlined } from '@mui/icons-material';

import { useAuth } from 'contexts/AuthContext';
import { useCategory } from 'hooks/queries/useArticles';
import { useToverHead } from 'hooks/useToverHead';
import CenteredCircularProgress from 'components/page/CenteredCircularProgress';
import { DefaultLayout } from 'components/page/layouts/DefaultLayout';
import PageHeaderSection from 'components/page/PageHeaderSection';
import PageSectionContainer from 'components/page/PageSectionContainer';

interface CategoryContainerProps {
  // Already resolved elsewhere (e.g. ContentPage) — skip the internal fetch.
  category?: ZendeskCategory;
}

const CategoryContainer = ({ category: preloadedCategory }: CategoryContainerProps) => {
  const { i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();

  const categoryId = preloadedCategory ? '' : id ?? '';
  const { data: fetchedCategory, isLoading } = useCategory(categoryId, i18n.language);
  const category = preloadedCategory ?? fetchedCategory;

  useToverHead({
    title: category?.name ?? '',
    description: category?.meta_description,
    alternateLinks: category?.alt_links,
    noIndex: !isLoading && !category,
  });

  const signinToRead = (isPublic: boolean) => (auth.state.account && auth.can('access.mytover') ? false : !isPublic);

  return isLoading ? (
    <CenteredCircularProgress />
  ) : category ? (
    <DefaultLayout>
      <PageHeaderSection
        titleId="category-title"
        crumbs={category.breadcrumbs}
        description={category.description}
        title={category.name}
      />
      <PageSectionContainer sx={{ pb: 4 }}>
        <ImageList id="category-articles" rowHeight="auto" cols={1}>
          {category.sub_categories &&
            category.sub_categories.map((category: ZendeskCategory, index: number) => (
              <ImageListItem key={`article-tile-${index}`}>
                <Divider />
                <ListItem id={`category-subcategory-${category.id}`} component={RouterLink} to={category.route}>
                  <ListItemIcon>
                    <FolderOpenOutlined />
                  </ListItemIcon>
                  <ListItemText>{category.name}</ListItemText>
                </ListItem>
              </ImageListItem>
            ))}
          {category.articles &&
            category.articles.map((article: ZendeskArticle, index: number) => (
              <ImageListItem key={`article-tile-${index}`}>
                <Divider />
                <ListItemButton
                  id={`category-article-${article.id}`}
                  // A reserved article stays listed so the reader can see what the My Tover
                  // service offers; data-locked is the state, independent of how it looks.
                  data-locked={signinToRead(article.public)}
                  disabled={signinToRead(article.public)}
                  component={RouterLink}
                  to={article.route}
                >
                  <ListItemIcon>
                    <DescriptionOutlined />
                  </ListItemIcon>
                  <ListItemText>{article.title}</ListItemText>
                </ListItemButton>
              </ImageListItem>
            ))}
        </ImageList>
      </PageSectionContainer>
    </DefaultLayout>
  ) : null;
};

export default CategoryContainer;
